import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';

const QUARTERS = [1, 2, 3, 4];
const ROW_MIN_HEIGHT = 54;

function currentYearQuarter() {
  const now = new Date();
  return { year: now.getFullYear(), quarter: Math.floor(now.getMonth() / 3) + 1 };
}

const QuarterReview = () => {
  const { hasPermission } = useAuth();
  const modal = useModal();
  const canEdit = hasPermission('received_invoices', 'edit');

  const initial = currentYearQuarter();
  const [year, setYear] = useState(initial.year);
  const [quarter, setQuarter] = useState(initial.quarter);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], unassigned: { thisQuarter: 0, total: 0 } });
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/quarter-review?year=${year}&quarter=${quarter}`);
      const rows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      setData({ rows, unassigned: res.data?.unassigned || { thisQuarter: 0, total: 0 } });
      const d = {};
      rows.forEach(r => { d[r.providerId] = r.expectedManual != null ? String(r.expectedManual) : ''; });
      setDrafts(d);
    } catch (error) {
      modal.error('Error cargando el repaso trimestral');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, quarter]);

  useEffect(() => { load(); }, [load]);

  const saveExpected = async (row) => {
    const raw = (drafts[row.providerId] ?? '').trim();
    const stored = row.expectedManual != null ? String(row.expectedManual) : '';
    if (raw === stored) return;
    if (!canEdit) return;
    const n = parseInt(raw, 10);
    if (raw !== '' && (!Number.isInteger(n) || n < 0)) {
      modal.error('Las facturas esperadas deben ser un número entero ≥ 0');
      return;
    }
    try {
      await api.put(`/providers/${row.providerId}`, { expectedInvoicesPerQuarter: raw === '' ? null : n });
      await load();
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error guardando las facturas esperadas');
    }
  };

  const toggleReviewed = async (row) => {
    if (!canEdit) return;
    try {
      await api.post(`/quarter-review/${row.providerId}`, { year, quarter, reviewed: !row.reviewed });
      setData(prev => ({ ...prev, rows: prev.rows.map(r => (r.providerId === row.providerId ? { ...r, reviewed: !r.reviewed } : r)) }));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error marcando el proveedor');
    }
  };

  const rows = data.rows;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Repaso trimestral</h1>
          <p className="page-subtitle">Compara, por proveedor, las facturas que esperas con las que ya has aportado este trimestre.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div>
            <label className="form-label" style={{ display: 'block' }}>Año</label>
            <input type="number" className="form-input" style={{ width: 100 }} value={year}
              onChange={e => setYear(parseInt(e.target.value, 10) || initial.year)} />
          </div>
          <div>
            <label className="form-label" style={{ display: 'block' }}>Trimestre</label>
            <select className="form-input" style={{ width: 120 }} value={quarter} onChange={e => setQuarter(parseInt(e.target.value, 10))}>
              {QUARTERS.map(q => <option key={q} value={q}>{q}º trimestre</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card"><div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div></div>
      ) : rows.length === 0 ? (
        <div className="card"><div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>No hay proveedores recurrentes ni facturas de proveedores en este trimestre.</div></div>
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Panel: esperadas */}
          <div className="card" style={{ flex: '1 1 380px', minWidth: 320 }}>
            <h2 className="card-title" style={{ marginBottom: 4 }}>Esperadas por trimestre</h2>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 12 }}>
              Si lo dejas vacío, se usa el estimado del histórico. Si pones un número, manda el tuyo.
            </p>
            {rows.map(row => (
              <div key={row.providerId} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: ROW_MIN_HEIGHT, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: canEdit ? 'pointer' : 'default' }}>
                  <input type="checkbox" checked={row.reviewed} disabled={!canEdit} onChange={() => toggleReviewed(row)} title="Revisado / correcto" />
                </label>
                <span style={{ flex: 1, fontSize: '0.9rem', fontWeight: 500 }}>{row.name}</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <input
                    type="number" min="0" step="1"
                    className="form-input" style={{ width: 80, textAlign: 'right' }}
                    placeholder={String(row.estimate)}
                    value={drafts[row.providerId] ?? ''}
                    disabled={!canEdit}
                    onChange={e => setDrafts(d => ({ ...d, [row.providerId]: e.target.value }))}
                    onBlur={() => saveExpected(row)}
                  />
                  {(drafts[row.providerId] ?? '') === '' && (
                    <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>estimado: {row.estimate}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Panel: aportadas */}
          <div className="card" style={{ flex: '1 1 380px', minWidth: 320 }}>
            <h2 className="card-title" style={{ marginBottom: 4 }}>Aportadas en el {quarter}º trimestre de {year}</h2>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 12 }}>
              Facturas recibidas que cuentan en este trimestre. En rojo, los proveedores a los que les faltan; en verde, los que ya están al día.
            </p>
            {rows.map(row => {
              const deficit = row.expected - row.contributedThisQuarter;
              const faltan = deficit > 0;
              const extra = row.contributedThisQuarter - row.expected; // >0 si hay de más
              const color = faltan ? '#dc2626' : '#16a34a';
              return (
                <div key={row.providerId} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: ROW_MIN_HEIGHT, padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: canEdit ? 'pointer' : 'default' }}>
                    <input type="checkbox" checked={row.reviewed} disabled={!canEdit} onChange={() => toggleReviewed(row)} title="Revisado / correcto" />
                  </label>
                  <span style={{ flex: 1, fontSize: '0.9rem', fontWeight: 500 }}>{row.name}</span>
                  <span style={{ textAlign: 'right' }}>
                    <span style={{ fontWeight: 700, color }}>{row.contributedThisQuarter}</span>
                    <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}> / {row.expected} esperadas</span>
                    {faltan
                      ? <div style={{ fontSize: '0.72rem', color: '#dc2626' }}>faltan {deficit}</div>
                      : <div style={{ fontSize: '0.72rem', color: '#16a34a' }}>al día{extra > 0 ? ` (${extra} de más)` : ''}</div>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 className="card-title" style={{ marginBottom: 8 }}>Sin proveedor asignado</h2>
          <p style={{ margin: '0 0 8px', color: '#64748b' }}>
            {data.unassigned.thisQuarter} factura(s) de este trimestre y {data.unassigned.total} en total no tienen proveedor vinculado
            {' '}(p. ej. emisores sin NIF español: GitHub, Anthropic, HP…), por lo que no salen arriba como proveedor.
            {' '}Vincúlalas o dales de alta como proveedor en <Link to="/received-invoices">Facturas entrantes</Link> para que cuenten en el repaso.
          </p>
          {Array.isArray(data.unassigned.byName) && data.unassigned.byName.length > 0 && (
            <div>
              {data.unassigned.byName.map(u => (
                <div key={u.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ flex: 1, fontSize: '0.88rem', fontWeight: 500 }}>{u.name}</span>
                  <span style={{ textAlign: 'right' }}>
                    <span style={{ fontWeight: 700, color: u.thisQuarter > 0 ? '#0f172a' : '#94a3b8' }}>{u.thisQuarter}</span>
                    <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}> este trim. · {u.total} total</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuarterReview;
